import type { AudiologyResult, LeadSource, Quantum, ReviewStatus } from './mhlTypes'

export const AUDIOLOGY_LABELS: Record<AudiologyResult, string> = {
    '>=4dB': 'Greater than 4 dB',
    '<4dB': 'Less than 4 dB NIHL',
    tbc: 'TBC',
}

export const AUDIOLOGY_LONG_LABELS: Record<AudiologyResult, string> = {
    '>=4dB': 'Greater than 4 dB',
    '<4dB': 'Less than 4 dB NIHL',
    tbc: 'TBC — not calculated yet',
}

export const AUDIOLOGY_OPTIONS: { value: AudiologyResult; label: string }[] = [
    { value: '<4dB', label: 'Less than 4 dB NIHL' },
    { value: '>=4dB', label: 'Greater than 4 dB' },
    { value: 'tbc', label: 'TBC' },
]

export const QUANTUM_LABELS: Record<'above' | 'below' | 'tbc', string> = {
    above: 'Above quantum',
    below: 'Below quantum',
    tbc: 'Quantum TBC',
}

export const REVIEW_LABELS: Record<ReviewStatus, string> = {
    awaiting_review: 'Awaiting review',
    approved: 'Approved',
    rejected: 'Rejected',
    not_required: 'No review required',
}

export const SOURCE_LABELS: Record<LeadSource, string> = {
    webform: 'Existing client base',
    dashboard: 'Onboarded via dashboard',
}

/**
 * Where a claim-site visitor or webform lead came from, as tagged by the claim
 * site: 'organic', a partner tag such as 'sentinelforces', or a raw utm_source.
 * Visits and leads from before the tagging went live have no source.
 */
export const REFERRAL_LABELS: Record<string, string> = {
    sentinelforces: 'Sentinel Forces',
    organic: 'Organic',
}

export const UNKNOWN_REFERRAL = 'unknown'

export const referralLabel = (value: string | null | undefined) =>
    !value || value === UNKNOWN_REFERRAL ? 'Unknown' : REFERRAL_LABELS[value] || value

export const referralTone = (value: string | null | undefined) =>
    !value || value === UNKNOWN_REFERRAL ? 'muted' : value === 'organic' ? 'info' : 'brand'

/** Filter values: 'all', or a source from REFERRAL_LABELS plus any others seen in the data. */
export const referralFilterOptions = (seen: Array<string | null | undefined> = []) => {
    const extras = seen.filter((s): s is string => !!s && s !== UNKNOWN_REFERRAL && !(s in REFERRAL_LABELS))
    const values = [...Object.keys(REFERRAL_LABELS), ...Array.from(new Set(extras)).sort()]
    return [{ value: 'all', label: 'All sources' }, ...values.map((value) => ({ value, label: referralLabel(value) }))]
}

export const audiologyLabel = (value: AudiologyResult | string | null | undefined) =>
    value && value in AUDIOLOGY_LABELS ? AUDIOLOGY_LABELS[value as AudiologyResult] : '—'

export const quantumLabel = (value: Quantum | string | null | undefined) =>
    value === 'above' ? QUANTUM_LABELS.above : value === 'below' ? QUANTUM_LABELS.below : QUANTUM_LABELS.tbc

export const reviewLabel = (value: ReviewStatus | string | null | undefined) =>
    value && value in REVIEW_LABELS ? REVIEW_LABELS[value as ReviewStatus] : '—'

/** Class-name suffix used by the shared pill styles. */
export const audiologyTone = (value: AudiologyResult | string | null | undefined) =>
    value === '>=4dB' ? 'good' : value === '<4dB' ? 'bad' : 'warn'

export const reviewTone = (value: ReviewStatus | string | null | undefined) =>
    value === 'approved' ? 'good' : value === 'rejected' ? 'bad' : value === 'awaiting_review' ? 'warn' : 'muted'

export const esignTone = (signed: boolean, linkSent: boolean) => (signed ? 'good' : linkSent ? 'info' : 'muted')

export const esignLabel = (signed: boolean, linkSentAt: string | null, linkOpenedAt?: string | null) =>
    signed ? 'Signed' : linkOpenedAt ? 'Link opened' : linkSentAt ? 'Link sent' : 'Not sent'

export const formatDate = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const formatDateTime = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const formatRelative = (value: string | null | undefined) => {
    if (!value) return '—'
    const then = new Date(value).getTime()
    if (Number.isNaN(then)) return String(value)
    const diffMs = Date.now() - then
    const minutes = Math.round(diffMs / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`
    const days = Math.round(hours / 24)
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
    return formatDate(value)
}

export const formatBytes = (bytes: number | null | undefined) => {
    if (!bytes && bytes !== 0) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const yesNo = (value: number | boolean | null | undefined) =>
    value === null || value === undefined ? '—' : value ? 'Yes' : 'No'

export const fullName = (parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' ').trim() || '—'

export const formatMoney = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = typeof value === 'number' ? value : parseFloat(String(value))
    if (Number.isNaN(numeric)) return '—'
    return numeric.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
}

export const agentDisplay = (name: string | null | undefined, email: string | null | undefined) =>
    name?.trim() || email?.trim() || '—'

/**
 * Splits a stored reviewer string ("Jane Doe <jane@x.com>" or a bare email)
 * into display parts so the email can be rendered smaller / muted on its own
 * line instead of wrapping untidily.
 */
export const splitPersonLabel = (value: string | null | undefined): { name: string; email: string | null } => {
    const raw = (value || '').trim()
    if (!raw) return { name: '—', email: null }
    const match = raw.match(/^(.*?)\s*<(.+?)>$/)
    if (match) return { name: match[1].trim() || match[2].trim(), email: match[1].trim() ? match[2].trim() : null }
    return { name: raw, email: null }
}
